import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { SIN_AVISO_EN_403 } from '../core/interceptors/error.interceptor';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { TerminalDeLaSesion } from '../core/offline/terminal-de-la-sesion';
import { negocioDeLaSesionAbierta } from '../core/tenant/negocio-del-token';

/** Una persona que puede consumir (-mt V233, `GET /api/consumo-propio/personas`). El dueño solo le llega al admin. */
export type PersonaDelConsumo = { tipo: 'DUENO'; nombre: string } | { tipo: 'EMPLEADO'; employeeId: number; nombre: string };

export interface ConsumoParaRegistrar {
    idempotencyKey: string;
    terminalId: string | null;
    persona: { tipo: 'DUENO' } | { tipo: 'EMPLEADO'; employeeId: number };
    /** En lo que se vende por peso, `quantity` son gramos (M19c), como en la venta. */
    items: { productId: string; quantity: number }[];
    /**
     * Z30 (-mt V239): solo la dotación legal, y solo de un empleado. Sin la clave, -mt toma CONSUMO; se omite en el consumo
     * normal para que un -mt anterior a V239 no dé 400 por campo desconocido (fail-on-unknown).
     */
    motivo?: 'DOTACION_LEGAL';
}

/** Un consumo registrado, como lo da -mt (POST 201 y GET del informe, §9.3 y §9.5). */
export interface ConsumoRegistrado {
    uuid: string;
    idOrder: number;
    persona: { tipo: 'DUENO' | 'EMPLEADO'; employeeId?: number; nombre: string };
    /** `porPeso` (Agente-mt, próximo commit): con él, `cantidad` son gramos. Sin la clave, se pinta tal cual. */
    items: { productId: string; nombre: string; cantidad: number; precio: number; porPeso?: boolean }[];
    totalReferencia: number;
    registradoPor: string | null;
    creadoEn: string;
    anuladoEn?: string | null;
    motivo?: string | null;
}

/** Lo que un 4xx dejó sin registrar: se guarda aparte, nunca se tira (no se reintenta: daría lo mismo). */
interface Rechazado { consumo: ConsumoParaRegistrar; codigo: string | null; en: string }
/** Un consumo esperando red. La fecha va FUERA del cuerpo: -mt rechaza campos que no conoce (fail-on-unknown). */
interface Pendiente { consumo: ConsumoParaRegistrar; en: string }

/** Un pendiente que lleva más de esto sin poder enviarse cuenta como «no se ha podido registrar» (DrDev002). */
const UN_DIA_MS = 24 * 60 * 60 * 1000;

const CLAVE = 'suresell.consumo-propio';

/**
 * Z23 · consumo propio del dueño o de un empleado (-mt V233; diseño docs fd92e14 y §9 24b9e1d).
 *
 * - **Se ofrece** si `personas` contesta 200. Con el interruptor de -mt apagado da 409 CONSUMO_PROPIO_APAGADO y un -mt
 *   sin la ruta da 404: en los dos casos `personas()` queda en null y el POS no pinta nada (se puede servir antes que -mt).
 * - **Sin red**, una cola APARTE que solo reintenta el POST con su `idempotencyKey`. NO entra en la cadena del hash del
 *   outbox (contrato con Java): el consumo no mueve dinero de la caja (§9.1).
 */
@Injectable({ providedIn: 'root' })
export class ConsumoPropioService {
    private readonly http = inject(HttpClient);
    private readonly terminal = inject(TerminalDeLaSesion);
    private readonly base = `${environment.apiBaseUrl}/api/consumo-propio`;

    /** null = el servidor no lo ofrece (o aún no se sabe). */
    readonly personas = signal<PersonaDelConsumo[] | null>(null);
    /** Rechazados más pendientes de más de un día: productos que salieron sin registrarse. El POS lo avisa siempre. */
    readonly sinRegistrar = signal(0);
    private reintentando = false;

    constructor() {
        if (typeof window !== 'undefined') window.addEventListener('online', () => void this.reintentar());
        this.contar();
    }

    /**
     * Solo pregunta si -mt lo ofrece (personas 200), sin tocar la cola: es lo que necesita el menú (Z167). Con cualquier rol
     * que abra el menú, `reintentar()` mandaría la cola y un 403 SOLO_ADMINISTRADOR o un 401 la pasaría a «rechazados».
     */
    async pedirPersonas(): Promise<void> {
        try {
            this.personas.set(await firstValueFrom(this.http.get<PersonaDelConsumo[]>(`${this.base}/personas`)));
        } catch {
            this.personas.set(null); // 409 apagado, 404 sin la ruta, 403 un rol que no registra o sin red: no se ofrece
        }
    }

    /** La caja: la señal y, además, la cola pendiente. */
    async cargarPersonas(): Promise<void> {
        this.contar();
        await this.pedirPersonas();
        void this.reintentar();
    }

    async nuevo(persona: ConsumoParaRegistrar['persona'], items: ConsumoParaRegistrar['items']): Promise<ConsumoParaRegistrar> {
        return { idempotencyKey: crypto.randomUUID(), terminalId: await this.terminal.id(), persona, items };
    }

    /** El consumo como lo registró -mt, o `'en-cola'` si no hubo red (se manda solo al volver). Un 4xx o 5xx se lanza tal cual. */
    async registrar(consumo: ConsumoParaRegistrar): Promise<ConsumoRegistrado | 'en-cola'> {
        try {
            return await firstValueFrom(this.http.post<ConsumoRegistrado>(this.base, consumo));
        } catch (e) {
            if (e instanceof HttpErrorResponse && e.status === 0) {
                this.guardar('pendientes', [...this.leer<Pendiente>('pendientes'), { consumo, en: new Date().toISOString() }]);
                return 'en-cola';
            }
            throw e;
        }
    }

    /** Manda la cola en orden. Sin red, para y espera al próximo intento; un 4xx sale de la cola y se guarda aparte. */
    async reintentar(): Promise<void> {
        if (this.reintentando) return;
        this.reintentando = true;
        try {
            for (const { consumo } of this.leer<Pendiente>('pendientes')) {
                try {
                    // Sin aviso en 403: el consumo se queda esperando, y el aviso saldría en cada arranque (Z168).
                    await firstValueFrom(this.http.post(this.base, consumo, { context: new HttpContext().set(SIN_AVISO_EN_403, true) }));
                } catch (e) {
                    const status = e instanceof HttpErrorResponse ? e.status : 0;
                    // Sin red o con el servidor caído, espera al próximo intento. Un 401 (sesión vencida o cerrada) o un 403
                    // (un rol que no registra: el bodeguero da ROL_SIN_ACCESO y un mesero SOLO_ADMINISTRADOR) tampoco rechaza el
                    // consumo: dice quién está dentro, y lo manda la caja después. Solo un rechazo del contenido (400, 404, 409,
                    // 422…) sale de la cola. Sin lista de roles aquí: quién puede lo dice el servidor (Z168, DrDev002).
                    if (status === 0 || status >= 500 || status === 401 || status === 403) return;
                    const codigo = e instanceof HttpErrorResponse ? (e.error?.codigo ?? null) : null;
                    this.guardar('rechazados', [...this.leer<Rechazado>('rechazados'), { consumo, codigo, en: new Date().toISOString() }]);
                }
                this.guardar('pendientes', this.leer<Pendiente>('pendientes').filter((p) => p.consumo.idempotencyKey !== consumo.idempotencyKey));
            }
        } finally {
            this.reintentando = false;
        }
    }

    /** El informe, entre dos días (AAAA-MM-DD). */
    listar(desde: string, hasta: string): Promise<ConsumoRegistrado[]> {
        return firstValueFrom(this.http.get<ConsumoRegistrado[]>(this.base, { params: { desde, hasta } }));
    }

    /** Solo el admin (403 si no). Repetirlo da 200 igual: es idempotente. */
    anular(uuid: string, motivo: string): Promise<ConsumoRegistrado> {
        return firstValueFrom(this.http.post<ConsumoRegistrado>(`${this.base}/${encodeURIComponent(uuid)}/anular`, { motivo }));
    }

    pendientes(): number {
        return this.leer('pendientes').length;
    }

    /** Recalcula {@link sinRegistrar}: al arrancar, al cargar las personas y cada vez que cambia una lista. */
    contar(ahora = Date.now()): void {
        const viejos = this.leer<Pendiente>('pendientes').filter((p) => ahora - Date.parse(p.en) > UN_DIA_MS).length;
        this.sinRegistrar.set(this.leer('rechazados').length + viejos);
    }

    /** Por negocio: una cola de un negocio nunca sale con la sesión de otro. */
    private clave(lista: 'pendientes' | 'rechazados'): string | null {
        const negocio = negocioDeLaSesionAbierta();
        return negocio ? `${CLAVE}.${lista}.${negocio}` : null;
    }

    private leer<T>(lista: 'pendientes' | 'rechazados'): T[] {
        const k = this.clave(lista);
        try {
            const v = k ? JSON.parse(localStorage.getItem(k) ?? '[]') : [];
            return Array.isArray(v) ? v : [];
        } catch {
            return [];
        }
    }

    private guardar(lista: 'pendientes' | 'rechazados', valor: unknown[]): void {
        const k = this.clave(lista);
        if (!k) return;
        try {
            localStorage.setItem(k, JSON.stringify(valor));
        } catch { /* sin almacenamiento: queda en memoria del intento */ }
        this.contar();
    }
}
